import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { App } from "./App";
import { BetaApp } from "./components/BetaApp";
import { AuthGate } from "./components/AuthGate";
import { LoginPage } from "./pages/LoginPage";
import { SignupPage } from "./pages/SignupPage";
import { VerifyPage } from "./pages/VerifyPage";
import { ForgotPage } from "./pages/ForgotPage";
import { ResetPage } from "./pages/ResetPage";
import { AdminUsersPage } from "./pages/AdminUsersPage";
import { AdminEventsPage } from "./pages/AdminEventsPage";
import { MobileIndex } from "./mobile/MobileIndex";
import { PrototypeA } from "./mobile/prototypes/A_Exec";
import { PrototypeB } from "./mobile/prototypes/B_Sheet";
import { PrototypeC } from "./mobile/prototypes/C_Rank";
import { PrototypeD } from "./mobile/prototypes/D_Faith";
import { PrototypeE } from "./mobile/prototypes/E_Health";
import { SurgeryMobile } from "./mobile/prototypes/Surgery";
import { RouteMobile } from "./mobile/prototypes/Route";
import { PlMobile } from "./mobile/prototypes/Pl";
import { KbRevenueMobile } from "./mobile/prototypes/KbRevenue";
import { SurgeryRevenueMobile } from "./mobile/prototypes/SurgeryRevenue";
// [2026-08-03] 和文フォントを同梱して端末差をなくす (経緯と実測は styles.css の該当ブロック)。
//   ★分割サブセット (400.css / 700.css = 120 スライス) ではなく **japanese 単一ファイル** を使う。
//     人名漢字は Unicode 上に散るので、分割だと実データで 76 スライス必要になり逆に重い。
//   ★latin を先に import する: latin 側は unicode-range 付きなので英数字はそちらが当たり、
//     和文だけ 1MB のファイルから引く形になる。
//   ★Vite が woff2 を dist/assets へバンドルする = 同一オリジン配信 = オフライン院内端末でも動く。
import "@fontsource/noto-sans-jp/latin-400.css";
import "@fontsource/noto-sans-jp/latin-700.css";
import "@fontsource/noto-sans-jp/japanese-400.css";
import "@fontsource/noto-sans-jp/japanese-700.css";
import "./styles.css";

// [2026-07-21] deploy でチャンク名 (hash) が変わると、開きっぱなしの古いページが
// dynamic import (exceljs 等) で旧チャンクを取りに行き 404 → 「ダウンロード失敗」になる。
// vite:preloadError を捕まえて自動リロード (新 index に載せ替える)。定番の stale chunk 対策。
window.addEventListener("vite:preloadError", (e) => {
  e.preventDefault();
  window.location.reload();
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime:   60_000,   // 1 分
      retry:        1,
      refetchOnWindowFocus: false,
    },
  },
});

// Former referral bookmarks now open the released tab before the beta permission gate.
function BetaEntry() {
  const location = useLocation();
  return new URLSearchParams(location.search).get("tab") === "紹介・入院分析"
    ? <Navigate to={`/${location.search}${location.hash}`} replace />
    : <AuthGate betaOnly><BetaApp /></AuthGate>;
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login"  element={<LoginPage  />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/verify" element={<VerifyPage />} />
          <Route path="/forgot" element={<ForgotPage />} />
          <Route path="/reset"  element={<ResetPage  />} />
          <Route path="/admin/users"  element={<AuthGate adminOnly><AdminUsersPage  /></AuthGate>} />
          <Route path="/admin/events" element={<AuthGate adminOnly><AdminEventsPage /></AuthGate>} />
          {/* モバイル版 — 全 user 公開 (A 系 4 page + 索引) */}
          <Route path="/m"           element={<AuthGate><MobileIndex /></AuthGate>} />
          <Route path="/m/a-exec"    element={<AuthGate><PrototypeA /></AuthGate>} />
          <Route path="/m/a-kbrev"   element={<AuthGate><KbRevenueMobile /></AuthGate>} />
          <Route path="/m/a-opesr"   element={<AuthGate><SurgeryRevenueMobile /></AuthGate>} />
          <Route path="/m/a-surgery" element={<AuthGate><SurgeryMobile /></AuthGate>} />
          <Route path="/m/a-route"   element={<AuthGate><RouteMobile /></AuthGate>} />
          <Route path="/m/a-pl"      element={<AuthGate><PlMobile /></AuthGate>} />
          {/* 没案 (B〜E) は admin only で参照用に残置 */}
          <Route path="/m/b-sheet"   element={<AuthGate adminOnly><PrototypeB /></AuthGate>} />
          <Route path="/m/c-rank"    element={<AuthGate adminOnly><PrototypeC /></AuthGate>} />
          <Route path="/m/d-faith"   element={<AuthGate adminOnly><PrototypeD /></AuthGate>} />
          <Route path="/m/e-health"  element={<AuthGate adminOnly><PrototypeE /></AuthGate>} />
          {/* /beta は未リリースタブ。/theta は正式昇格前のブックマーク互換として残す。 */}
          <Route path="/beta"   element={<BetaEntry />} />
          <Route path="/theta"  element={<AuthGate><App /></AuthGate>} />
          <Route path="/*"      element={<AuthGate><App /></AuthGate>} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
